import React, { useMemo, useState } from 'react';
import {
  OneWayInOpportunitySimpleV2Dto,
  OneWayInOpportunityStatusCreateDto,
  OneWayInStatus,
} from 'src/api/v2';
import { Button, Dialog, Checkbox, Textfield, Input, Label, Alert } from '@innovationnorway/cl-react';
import { useTranslation } from 'react-i18next';
import { ErrorMessages } from '../constants';
import { Link } from 'react-router-dom';
import useServiceTemplatesList from '../../hooks/use-service-templates';
import { SERVICES } from 'src/features/services';
import useOneWayInServices from '../../hooks/use-one-way-in-services';
import ToastContent from 'src/components/ToastContent/ToastContent';
import { toast } from 'react-toastify';
import useCreateOpportunity from '../../hooks/use-create-opportunity';
import { useUser } from 'src/features/authorization';
import useSiteimprove from 'src/hooks/use-siteimprove';
import { SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';
import { handleSelectedServicesChange } from '../helpers';
import { useCluster } from 'src/features/cluster';
import useOneWayInMultiple from '../../hooks/use-one-way-in-multiple';

interface DeliverServicesModalProps {
  dialogOpen: boolean;
  onClose: () => void;
  opportunities: OneWayInOpportunitySimpleV2Dto[];
  initialSelectedServiceIds?: string[];
}

const DeliverServicesModal = ({
  dialogOpen,
  onClose,
  opportunities,
  initialSelectedServiceIds: selectedServiceIdsProp,
}: DeliverServicesModalProps) => {
  const [note, setNote] = useState('');
  const [deliveredDate, setDeliveredDate] = useState<Date>(new Date());
  const [errorMessage, setErrorMessage] = useState<ErrorMessages | null>(null);
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tCommon } = useTranslation('common');
  const { allServiceTemplates } = useServiceTemplatesList();
  const { cluster } = useCluster();
  const { activeClusterServices, isLoading } = useOneWayInServices(cluster?.id);
  const { createOpportunity } = useCreateOpportunity();
  const { user, displayName, isAadUser } = useUser();
  const siteimprove = useSiteimprove();
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>(selectedServiceIdsProp || []);
  const { deliverServiceBatch, deliverService } = useOneWayInMultiple(
    opportunities.map((r) => r.oneWayInId),
    dialogOpen,
  );

  const firstTemplate = useMemo(() => {
    return allServiceTemplates ? allServiceTemplates[0] : undefined;
  }, [allServiceTemplates]);

  const handleCloseModal = () => {
    setNote('');
    setErrorMessage(null);
    setDeliveredDate(new Date());
    onClose();
  };

  const onDeliverService = async () => {
    if (selectedServiceIds.length === 0) {
      setErrorMessage(ErrorMessages.NoServicesSelected);
      return;
    }

    if (note.trim() === '') {
      setErrorMessage(ErrorMessages.FieldEmpty);
      return;
    }

    const deliveries = opportunities.map(async (opportunity) => {
      // Create opportunity if it does not exist
      if (!opportunity.clusterId && !!cluster) {
        const createDto = {
          clusterId: cluster.id,
          oneWayInId: opportunity.oneWayInId,
          title: opportunity.title ?? '',
          municipality: opportunity.municipality ?? '',
          customerName: opportunity.customerName,
          organizationName: opportunity.organizationName,
          organizationNumber: opportunity.organizationNumber,
          submittedOn: opportunity.submittedOn,
        };
        const created = await createOpportunity.mutateAsync(createDto);
        if (created) {
          opportunity.id = created.id;
        }
      }

      const statusUpdate = {
        oneWayInOpportunityId: opportunity?.id,
        referenceClusterId: undefined,
        status: OneWayInStatus.Active,
        description: note,
        closedReason: undefined,
        registeredServices: selectedServiceIds,
        recommendedServices: [],
        clusterContactId: undefined,
        clusterUserId: isAadUser ? undefined : user?.clusterUserId,
        handlersName: displayName || undefined,
        sendFollowUpReminderOn: undefined,
        admittedToProgram: false,
        sivaPhase: undefined,
        deliveredOn: deliveredDate,
      } as OneWayInOpportunityStatusCreateDto;

      return {
        statusUpdate,
        formId: opportunity.oneWayInId,
      };
    });

    deliverServiceBatch(await Promise.all(deliveries))
      .then(() => {
        siteimprove.log(
          SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.id,
          SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.actions.SEND_DELIVERY,
          SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.labels.SUCCESS,
        );
        handleCloseModal();
        toast(
          <ToastContent
            title={tOneWayIn('toasts.serviceDelivery.success.title')}
            text={tOneWayIn('toasts.activityLogUpdated')}
            success={true}
          />,
          {
            toastId: 'ServiceDelivered',
            autoClose: 1000 * 15,
            position: 'bottom-center',
          },
        );
      })
      .catch(() => {
        siteimprove.log(
          SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.id,
          SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.actions.SEND_DELIVERY,
          SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.labels.ERROR,
        );
        setErrorMessage(ErrorMessages.SomethingWentWrong);
      });
  };

  const dialogContentNoServices = (
    <>
      <div className="stack-small padding-x--3 padding-bottom--1">
        <span className="dialog-with-sidebar--heading">
          {tOneWayIn('opportunityBoard.noServicesModal.title')}
        </span>
        <p className="font-size--medium">{tOneWayIn('opportunityBoard.noServicesModal.description')}</p>
      </div>
      <div className="dialog-with-sidebar--action-row">
        <Link to={firstTemplate ? `${SERVICES.BASE}/serviceTemplate/${firstTemplate?.id}` : SERVICES.BASE}>
          <Button>{tOneWayIn('button.addService')}</Button>
        </Link>
        <Button variant="secondary" onClick={handleCloseModal}>
          {tCommon('cancel')}
        </Button>
      </div>
    </>
  );

  const dialogContentWithServices = (
    <>
      <div className="stack-small padding-x--3 padding-bottom--1">
        <span className="dialog-with-sidebar--heading">
          {tOneWayIn('opportunityBoard.deliverServicesModal.title')}
        </span>
        <p className="font-size--medium">{tOneWayIn('opportunityBoard.deliverServicesModal.description')}</p>
      </div>
      <div className="dialog-with-sidebar--container recommend-services-modal--container display--flex flex-direction--row gap--2 padding--3 with-sidebar">
        <div className="recommend-services-modal--sidebar dialog-with-sidebar--side-bar font-size--medium">
          {activeClusterServices?.map((service) => (
            <div key={service.id}>
              <Checkbox
                label={service.name}
                checked={selectedServiceIds.includes(service.id)}
                onChange={(e) =>
                  setSelectedServiceIds(
                    handleSelectedServicesChange(service.id, e.target.checked, selectedServiceIds),
                  )
                }
              />
            </div>
          ))}
        </div>
        <div className="stack-medium padding--2 border-radius--1 background-color--white display--flex flex-direction--column flex--1 recommend-services-modal--message-editor">
          <div className="display--flex flex-direction--column gap--2 dialog-with-sidebar--form">
            <div className="display--flex flex-direction--column gap--1">
              <Label htmlFor="delivered-date">
                {tOneWayIn('opportunityBoard.deliverServicesModal.dateLabel')}
              </Label>
              <Input
                type="date"
                id="delivered-date"
                value={deliveredDate.toISOString().split('T')[0]}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => {
                  if (e.target.value) {
                    setDeliveredDate(new Date(e.target.value));
                  }
                }}
              />
            </div>
            <Textfield
              label={tCommon('note')}
              rows={3}
              multiline
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>
      </div>
      {errorMessage && (
        <div className="dialog-with-sidebar--alert--container">
          <Alert data-color="danger" className="dialog-with-sidebar--alert">
            {tOneWayIn(`errorMessage.${errorMessage}`)}
          </Alert>
        </div>
      )}
      <div className="dialog-with-sidebar--action-row">
        <Button
          onClick={onDeliverService}
          loading={deliverService.isLoading}
          disabled={deliverService.isLoading}
        >
          {tOneWayIn('opportunityBoard.deliverServicesModal.primaryButton')}
        </Button>
        <Button variant="secondary" onClick={handleCloseModal}>
          {tCommon('cancel')}
        </Button>
      </div>
    </>
  );

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleCloseModal}
      className="dialog-with-sidebar recommend-services-modal"
    >
      {activeClusterServices.length === 0 && !isLoading ? dialogContentNoServices : dialogContentWithServices}
    </Dialog>
  );
};

export default DeliverServicesModal;
